import { useState, useRef, useCallback } from 'react';
import { useEditorStore } from '../../../stores/editor.js';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

export interface AcceptResponseData {
  status: 'iterating';
  imageUrl: string;
  tokenCost: number;
  remainingTokens: number;
  acceptedAt: string;
  iterationsAccepted: number;
}

/**
 * Story 7.6: POST to /api/v1/edit/{sessionId}/accept on user acceptance.
 *
 * The browser calls this after the user clicks Accept in the review overlay.
 * The backend transitions the Redis session from 'awaiting_acceptance' to
 * 'completed' — only after this transition does the MCP's next /result poll
 * return the delivery payload. This hook is the *only* thing that causes the
 * result to leave the browser and reach the MCP client.
 *
 * On success, flips the local store to `submissionStatus: 'accepted'`. On
 * failure, surfaces the backend's error message via `submissionError` so the
 * user can retry or choose Keep editing. No client-side retry — a failed
 * accept is a user decision, not a transient fault worth hiding.
 */
export function useAcceptResult(sessionId: string | undefined) {
  const [isAccepting, setIsAccepting] = useState(false);
  // Synchronous re-entry guard. `isAccepting` (useState) is closure-captured
  // and a second synchronous invocation of `accept()` within the same tick
  // (React Strict-Mode double-fire, or a rapid double-dispatch before React
  // flushes the state update) would otherwise both read `isAccepting === false`
  // and both fire the POST. The ref is updated synchronously so any
  // same-tick re-entry is rejected.
  const inFlightRef = useRef(false);
  const acceptResult = useEditorStore((s) => s.acceptResult);
  const setSubmissionError = useEditorStore((s) => s.setSubmissionError);

  const accept = useCallback(async (): Promise<boolean> => {
    if (!sessionId || inFlightRef.current) return false;
    inFlightRef.current = true;
    setIsAccepting(true);
    setSubmissionError(null);

    const idempotencyKey = crypto.randomUUID();

    try {
      const res = await fetch(`${API_BASE}/v1/edit/${sessionId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({}),
      });

      if (res.ok) {
        acceptResult();
        return true;
      }

      const errBody = (await res.json().catch(() => ({}))) as {
        error?: { code?: string; message?: string };
      };
      const message = errBody.error?.message ?? 'Failed to deliver the result. Please try again.';
      setSubmissionError(message);
      return false;
    } catch {
      setSubmissionError('Network error while accepting the result. Please try again.');
      return false;
    } finally {
      inFlightRef.current = false;
      setIsAccepting(false);
    }
  }, [sessionId, acceptResult, setSubmissionError]);

  return { accept, isAccepting };
}
